import { collection, doc, getDocs, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { PACKAGES, INDIVIDUAL_RATES, VEHICLE_CATEGORIES } from "./constants";

/** Seed Firestore `packages` collection ONCE.
 *  Skips entirely if the collection already has documents — plan prices are
 *  admin-editable in Firestore and must never be reset back to code defaults. */
export async function seedPackages() {
  const pkgCol = collection(db, "packages");
  const existing = await getDocs(pkgCol);
  if (existing.size > 0) {
    return { seeded: 0, existed: existing.size, skipped: true };
  }
  const writes = PACKAGES.map((p) =>
    setDoc(doc(db, "packages", p.id), { ...p, createdAt: serverTimestamp() })
  );
  await Promise.all(writes);
  return { seeded: PACKAGES.length, existed: 0, skipped: false };
}

/** Add any `individual_rates` docs from INDIVIDUAL_RATES that don't exist yet
 *  in Firestore (e.g. a newly added service like the bike wash). Existing
 *  docs are left untouched so admin price edits made directly in Firestore
 *  are never overwritten. */
export async function seedIndividualRates() {
  const col = collection(db, "individual_rates");
  const existing = await getDocs(col);
  const existingIds = new Set(existing.docs.map((d) => d.id));
  const missing = INDIVIDUAL_RATES.filter((r) => !existingIds.has(r.id));
  if (missing.length === 0) return { added: 0 };
  await Promise.all(
    missing.map((r) => setDoc(doc(db, "individual_rates", r.id), { ...r, createdAt: serverTimestamp() }))
  );
  return { added: missing.length };
}

/** Add any `vehicleTypes` docs from VEHICLE_CATEGORIES that don't exist yet
 *  (e.g. a newly added category like "bike"). Existing docs are left
 *  untouched so admin edits made directly in Firestore are never overwritten. */
export async function seedVehicleTypes() {
  const col = collection(db, "vehicleTypes");
  const existing = await getDocs(col);
  const existingIds = new Set(existing.docs.map((d) => d.id));
  const missing = VEHICLE_CATEGORIES.filter((c) => !existingIds.has(c.id));
  if (missing.length === 0) return { added: 0 };
  // Pre-existing docs predate the `order` field and fall back to 99 when
  // sorted, so a newly added category gets a sentinel above that — it
  // appends after the existing ones instead of jumping to the front.
  await Promise.all(
    missing.map((c) =>
      setDoc(doc(db, "vehicleTypes", c.id), {
        label: c.label,
        short: c.short,
        priceKey: c.priceKey,
        order: 100 + VEHICLE_CATEGORIES.indexOf(c),
        createdAt: serverTimestamp(),
      })
    )
  );
  return { added: missing.length };
}
